import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { HttpParams } from "@angular/common/http";
import { PaginatorState } from "primeng/paginator";
import { ActivatedRoute, Router } from "@angular/router";
import {Table, TableColResizeEvent, TableLazyLoadEvent} from "primeng/table";
import { MedOptionType } from "@mk/med-ui";
import { SupplyController } from "@app/ui_controllers/supplyController";
import { SupplyResponse } from "@app/ui_models/web-response";
import { FilterService } from "@app/services/filter.service";
import { DatePipe } from "@angular/common";
import { DataService } from "@app/services/data.service";
import { AccountService } from "@app/services/account.service";
import {ReserveController} from "@app/ui_controllers/reserveController";

@Component({
  selector: "app-supplies",
  templateUrl: "./supplies.component.html",
  styleUrls: ["./supplies.component.scss", "../distributions/doz/distributions.doz.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class SuppliesComponent implements OnInit, OnDestroy {

  constructor(private readonly reserveController: ReserveController,
              private supplyController: SupplyController,
              private router: Router,
              private route: ActivatedRoute,
              private dataService: DataService,
              public filter: FilterService) {
  }

    @ViewChild("dt") dt: Table | undefined;
    tabHeader: { label: string; key: string; date: boolean; filter: boolean; joinTable: string; width: string }[] = tabHeader1;
    tableBody:  SupplyResponse[] = [];
    rows = 0;
    totalRows = 0;
    pageSize = 10;
    selectedPageNumber = 1;
    sortOrder: number | undefined | null = null;
    filterActive = true;
    filterField: string | string[] | null | undefined = "";
    tableState: TableLazyLoadEvent = {};
    loadingCounter = 0;
    showedOnPage = 10;
    filtersSubscriber: any;
    componentsLoading = false;
    components: any[] = [];
    readonly paginationOptions: MedOptionType<number>[] = PAGINATION_OPTIONS;
    directionOptions: any[] = [];

    ngOnInit(): void {
      if ("MOZ" === this.dataService.userRoleValue.getValue()) {
        this.router.navigate(["/reserves"]);
      }
      this.filtersSubscriber = this.filter.suppliesSubject.subscribe(() => {
        this.getSupplies(this.selectedPageNumber);
      });

      this.filter.supplies.expectedDate = null;
      this.filter.supplies.actualDate = null;

      this.loadColumnOrder();

      this.reserveController.getAllFilterOptions().subscribe(data => {
        this.directionOptions = data[0].options;
        this.filter.directionOptions = this.directionOptions;
      })
    }

    ngOnDestroy(): void {
      this.filtersSubscriber.unsubscribe();
    }

    saveColumnOrder() {
      localStorage.setItem("suppiesColumnOrder", JSON.stringify(this.tabHeader));
    }

    loadColumnOrder() {
      const savedOrder = localStorage.getItem("suppiesColumnOrder");
      if (savedOrder) {
        this.tabHeader = JSON.parse(savedOrder);
      }
    }

  private isLoading = false;

  getSupplies(
    page: number | undefined,
    order = "desc",
    field = "deliveryDate",
  ): void {
    if (this.isLoading) {
      return; // Prevent duplicate calls
    }

    this.isLoading = true; // Set loading flag
    let params = new HttpParams();
    this.selectedPageNumber = page ? page : 0;
    let sortFieldAndOrder = `${field},${order}`;
    if (["directions", "subdirections", "name"].includes(field)) {
      sortFieldAndOrder = "goods~" + sortFieldAndOrder;
    }
    params = params.append("page", this.selectedPageNumber - 1);
    params = params.append("size", this.pageSize);
    params = params.append("sort", sortFieldAndOrder);
    console.log("getSupplies method");

    this.supplyController.searchSuppliers(this.addFilterParams(params)).subscribe(data => {
      console.log("Supplies data: ", data);
      this.tableBody = data.content;
      this.totalRows = data.totalElements;
      this.isLoading = false; // Reset loading flag
    }, () => {
      this.isLoading = false; // Reset loading flag on error
    });
  }

    addFilterParams(params: HttpParams): HttpParams {
      const subscriber = this.filter.suppliesSubject.subscribe(data => {
        let counter = 0;
        if (data.mnn.length) {
          params = params.append(`conditions[${counter}].fieldName`, "goods~mnnName");
          params = params.append(`conditions[${counter}].values`, data.mnn.join(", "));
          params = params.append(`conditions[${counter}].operator`, "IN");
          counter++;
        }
        if (data.status.length) {
          params = params.append(`conditions[${counter}].fieldName`, "status");
          params = params.append(`conditions[${counter}].values`, data.status.join(", "));
          params = params.append(`conditions[${counter}].operator`, "IN");
          counter++;
        }
        if (data.direction.length) {
          params = params.append(`conditions[${counter}].fieldName`, "goods~direction");
          params = params.append(`conditions[${counter}].values`, data.direction.join(", "));
          params = params.append(`conditions[${counter}].operator`, "IN");
          counter++;
        }
        if (data.subDirection.length) {
          params = params.append(`conditions[${counter}].fieldName`, "goods~subDirection");
          params = params.append(`conditions[${counter}].values`, data.subDirection.join(", "));
          params = params.append(`conditions[${counter}].operator`, "IN");
          counter++;
        }
        if (data.agreementNum.length) {
          params = params.append(`conditions[${counter}].fieldName`, "agreementNum");
          params = params.append(`conditions[${counter}].value`, data.agreementNum[0]);
          params = params.append(`conditions[${counter}].operator`, "CONTAINS");
          counter++;
        }
        if (data.supplier.length) {
          params = params.append(`conditions[${counter}].fieldName`, "supplierName");
          params = params.append(`conditions[${counter}].value`, data.supplier[0]);
          params = params.append(`conditions[${counter}].operator`, "CONTAINS");
          counter++;
        }

        if (data.actualDate && data.actualDate.length) {
          const datepipe: DatePipe = new DatePipe("en-US");
          let formattedDate = datepipe.transform(new Date(data.actualDate[0]), "yyyy-MM-dd");
          params = params.append(`conditions[${counter}].fieldName`, "actualDeliveryDate");
          params = params.append(`conditions[${counter}].value`, formattedDate || "");
          params = params.append(`conditions[${counter}].operator`, "GREATER_THAN_EQUALS");
          counter++;

          formattedDate = datepipe.transform(new Date(data.actualDate[1]), "yyyy-MM-dd");
          params = params.append(`conditions[${counter}].fieldName`, "actualDeliveryDate");
          params = params.append(`conditions[${counter}].value`, formattedDate || "");
          params = params.append(`conditions[${counter}].operator`, "LESS_THAN_EQUALS");
          counter++;
        }

        if (data.expectedDate && data.expectedDate.length) {
          const datepipeExpected: DatePipe = new DatePipe("en-US");
          let formattedDate = datepipeExpected.transform(new Date(data.expectedDate[0]), "yyyy-MM-dd");
          params = params.append(`conditions[${counter}].fieldName`, "deliveryDate");
          params = params.append(`conditions[${counter}].value`, formattedDate || "");
          params = params.append(`conditions[${counter}].operator`, "GREATER_THAN_EQUALS");
          counter++;

          formattedDate = datepipeExpected.transform(new Date(data.expectedDate[1]), "yyyy-MM-dd");
          params = params.append(`conditions[${counter}].fieldName`, "deliveryDate");
          params = params.append(`conditions[${counter}].value`, formattedDate || "");
          params = params.append(`conditions[${counter}].operator`, "LESS_THAN_EQUALS");
          counter++;
        }

        if (data.warehouse.length) {
          params = params.append(`conditions[${counter}].fieldName`, "warehouseName");
          params = params.append(`conditions[${counter}].values`, data.warehouse.join(", "));
          params = params.append(`conditions[${counter}].operator`, "IN");
        }
      });
      subscriber.unsubscribe();
      return params;
    }

    getPathtpStatusImg(rowElement: string): string {
      let color: string;
      switch (rowElement) {
      case "Очікується": color = "yellow"; break;
      case "Затримка": color = "red"; break;
      case "Підготовка": color = "grey"; break;
      default: color = "green";
      }
      return `assets/icons/table/Statys_indicator_${color}.png`;
    }

    paginate($event: PaginatorState): void {
      this.selectedPageNumber = $event.page ? $event.page : 0;
      this.loadData(this.tableState);
    }

    getFirstShowedRowIndex(): number {
      return this.selectedPageNumber === 1 ? 0 : (this.selectedPageNumber - 1) * this.pageSize;
    }

    goToGoods(supplyId: string): void {
      if (this.dataService.userRoleValue.getValue() === "DOZ") {
        void (0);
      } else {
        this.router.navigate(["/supplies/supply/" + encodeURIComponent(supplyId)], { relativeTo: this.route });
      }
    }

    loadData($event: TableLazyLoadEvent, resetState = false) {
      this.tableState = $event;
      this.filterField = $event.sortField;
      if (resetState) {
        if ($event.sortField) {
          this.filterActive = this.sortOrder !== -1;
        } else {
          this.filterActive = false;
        }
      }
      if (this.sortOrder === -1 && resetState) {
        this.sortOrder = null;
        this.dt?.reset();
      } else {
        this.getSupplies(this.selectedPageNumber, $event.sortOrder === 1 ? "asc" : "desc", $event.sortField?.toString());
        this.sortOrder = $event.sortOrder;
      }
    }

    pageChange(pageNumber: number) {
      this.selectedPageNumber = pageNumber;
      this.loadData(this.tableState);
    }

    pageSizeChanged(pageSize: number) {
      this.pageSize = pageSize;
      this.selectedPageNumber = 1;
      this.loadData(this.tableState);
    }

    isVisibleFilters(): boolean {
      return this.filter.showSuppliesFilter;
    }

    getComponents(supplyId: string) {
      this.componentsLoading = true;
      this.supplyController.getComponents(supplyId).subscribe(data => {
        if (data && data.length > 0) {
          this.components = data[0];
        } else {
          this.components = [];
        }
        this.componentsLoading = false;
      })
    }

    // getTabHeader(): { label: string; key: string; date: boolean; filter: boolean; enum: boolean; joinTable: string; width: string } [] {
    //   return tabHeader1;
    // }

  handleColumnResize($event: TableColResizeEvent) {
    this.tabHeader.forEach(element => {
      if (element.label === $event.element.innerText.trim()) {
        console.log("element.width", element.width);
        element.width = $event.element.clientWidth + "px";
      }
    })
    this.saveColumnOrder();
  }

  getWithForColumn(key: string) {
    let width: string = "initial";
    this.tabHeader.filter(element => element.key === key).forEach(element => {
      width = element.width;
    })
    return width;
  }
}

export const PAGINATION_OPTIONS: MedOptionType<number>[] = [
  { label: "10", value: 10 },
  { label: "20", value: 20 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
];

export const tabHeader1 = [
  { key: "agreementNum", label: "Номер договору", date: false, filter: true, enum: false,  joinTable: "", width: "initial" },
  { key: "status", label: "Статус поставки", date: false, filter: true, enum: true, joinTable: "", width: "initial" },
  { key: "directions", label: "Напрям", date: false, filter: true, enum: true, joinTable: "goods", width: "initial" },
  { key: "subdirections", label: "Піднапрям", date: false, filter: true, enum: false, joinTable: "goods", width: "initial" },
  { key: "name", label: "МНН", date: false, filter: true, enum: false, joinTable: "goods", width: "initial" },
  { key: "deliveryDate", label: "Очікувана дата поставки", date: true, filter: true, enum: false, joinTable: "", width: "initial" },
  { key: "actualDeliveryDate", label: "Фактична дата", date: true, filter: true, enum: false, joinTable: "", width: "initial" },
  { key: "warehouseName", label: "Склад партнера", date: false, filter: true, enum: false, joinTable: "", width: "initial" },
  { key: "supplierName", label: "Постачальник", date: false, filter: true, enum: false, joinTable: "", width: "initial" },
  { key: "residency", label: "Резидентність", date: false, filter: true, enum: false, joinTable: "", width: "initial" },
  { key: "budgetYear", label: "Бюджетний рік", date: false, filter: true, enum: false, joinTable: "", width: "initial" },
  { key: "budgetName", label: "Бюджетна програма", date: false, filter: true, enum: false, joinTable: "", width: "initial" },
  { key: "supplyNum", label: "Номер поставки", date: false, filter: true, enum: false, joinTable: "", width: "initial" },
  { key: "logisticManager", label: "Відповідальний логіст", date: false, filter: true, enum: false, joinTable: "", width: "initial" },
];
